import { useContext } from 'react';
import { useParams } from 'react-router';
import { i18n } from 'app/utils/i18n';
import { Spinner } from 'app/components';
import Dropdown from 'app/components/Dropdown';
import { UserContext } from 'app/state/contexts';
import { useGetDocument } from 'app/api/documents/document-queries';
import { useStyles } from 'app/api/style-queries';
import { VariableDefinition } from '../variable-schema';
import { defaultFontOptions, FontItem, extractFontName } from '../utils/font-options';
import './VariableField.scss';

interface FontFieldProps {
  def: VariableDefinition;
  value: string | number;
  onChange: (key: string, value: string) => void;
  onFieldHover?: (key: string | null) => void;
  variables?: Record<string, unknown>;
}

const isSetFontFamily = (v: unknown): v is string => !!v && v !== 'null';

function inheritedFont(key: string, variables: Record<string, unknown> = {}): string {
  const single = variables.xrpFontFamily1;
  if (isSetFontFamily(single)) return single;
  if (key.endsWith('font-bold')) return isSetFontFamily(variables.xrpHeadingBold) ? variables.xrpHeadingBold : '';
  if (key.endsWith('font-italic')) return isSetFontFamily(variables.xrpHeadingItalic) ? variables.xrpHeadingItalic : '';
  return isSetFontFamily(variables.xrpHeadingRegular) ? variables.xrpHeadingRegular : '';
}

export function FontField({ def, value, onChange, onFieldHover, variables }: FontFieldProps) {
  const { documentId } = useParams<{ documentId: string }>();
  const { selectedOrganization } = useContext(UserContext);
  const pDocument = useGetDocument(documentId, selectedOrganization);
  const fontIds = pDocument.data?.content.design.filter((s: any) => s.type === 'font').map((font: any) => font.styleId) ?? [];
  const styleQuery = useStyles({ styleIds: fontIds, organization: selectedOrganization, documentRevisionId: pDocument.data?.revisionId });

  const hoverProps = onFieldHover ? { onMouseEnter: () => onFieldHover(def.key), onMouseLeave: () => onFieldHover(null) } : {};

  if (styleQuery.isLoading) {
    return <Spinner />;
  }

  const customFonts =
    styleQuery.data
      ?.map((style: any) => extractFontName(style.content))
      .filter(Boolean)
      .map((name: string) => ({ value: name, title: name })) ?? [];
  const items = [...defaultFontOptions, ...customFonts];
  const placeholder = inheritedFont(def.key, variables);

  return (
    <div className="variable-field d-flex flex-column gap-1" style={{ flex: '1 1 130px', minWidth: 0 }} {...hoverProps}>
      <label className="variable-field__label fs-7">{i18n(def.label)}</label>
      <Dropdown
        items={items}
        value={String(value)}
        onChange={(v: string) => onChange(def.key, v)}
        wrapper={FontItem}
        sm
        placeHolder={placeholder}
        disabled={false}
        groupbyKey={undefined}
        children={undefined}
        filter={false}
      />
    </div>
  );
}
